import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { type ChatPromptInput, type CreateChatInput } from '@aether/client-graphql/generated/graphql';
import { toast } from '@aether/ui/Toast';
import * as Sentry from '@sentry/react';

import { useAbilityContext } from '@/utils/auth';

import { useMutation, useQuery } from './client';
import {
  chatListQuery,
  createChatMutation,
  createChatPromptMutation,
  type ChatFragment,
  type ChatWithPromptsFragment,
} from './gql';
import { useDragonAssistantLayout } from './layout-context';

export const CURRENT_CHAT_KEY = 'currentChat';

interface AssistantChatContextType {
  /* 
    undefined while loading history
    null if explicitly set to a new chat
  */
  currentChat: string | null | undefined;
  history: ChatFragment[];
  historyLoading: boolean;
  setCurrentChat: (chatId: string | null) => void;
  createChat: (params: CreateChatInput) => Promise<ChatWithPromptsFragment>;
  addPrompt: (params: Omit<ChatPromptInput, 'chatId'>) => Promise<unknown>;

  // current context
  portfolioId?: string;
  benchmarkId?: string;
  startDate?: string;
  endDate?: string;
  currency?: string;
}

const AssistantChatContext = createContext<AssistantChatContextType | null>(null);

export function useDragonAssistantChat() {
  const context = useContext(AssistantChatContext);
  if (!context) {
    throw new Error('useDragonAssistantChat must be used within a DragonAssistantChatProvider');
  }
  return context;
}

export function DragonAssistantChatProvider({
  children,
  ...rest
}: {
  children: React.ReactNode;
  portfolioId?: string;
  benchmarkId?: string;
  startDate?: string;
  endDate?: string;
  currency?: string;
}) {
  const ability = useAbilityContext();
  const canWriteDragonAssistant =
    ability.can('create', 'DragonAssistantPage') || ability.can('update', 'DragonAssistantPage');
  const { open } = useDragonAssistantLayout();
  const [currentChat, _setCurrentChat] = useState<string | null | undefined>(() => {
    const current = localStorage.getItem(CURRENT_CHAT_KEY);
    return current ? (current === 'null' ? null : current) : undefined;
  });

  const [{ data, fetching }] = useQuery({
    query: chatListQuery,
    variables: { input: {} },
    requestPolicy: 'cache-and-network',
  });
  const [, execCreateChat] = useMutation(createChatMutation);
  const [, execAddPrompt] = useMutation(createChatPromptMutation);

  const historyRef = useRef<ChatFragment[] | undefined>(undefined);
  historyRef.current = data?.chats.data;

  const setCurrentChat = useCallback((chatId: string | null) => {
    if (chatId === null) {
      localStorage.setItem(CURRENT_CHAT_KEY, 'null');
      _setCurrentChat(null);
    } else {
      const chat = historyRef.current?.find(chat => chat.id === chatId);
      if (chat) {
        localStorage.setItem(CURRENT_CHAT_KEY, chatId);
        _setCurrentChat(chatId);
      }
    }
  }, []);

  const createChat = useCallback(
    async (params: CreateChatInput) => {
      if (!canWriteDragonAssistant) {
        toast.error('Trial ended. Please upgrade your account to continue using the Dragon Assistant.');
        return Promise.reject(new Error('User does not have permission to use the Dragon Assistant'));
      }
      return new Promise<ChatWithPromptsFragment>((resolve, reject) => {
        execCreateChat({ input: params }).then(result => {
          const { data, error } = result;
          if (error) {
            toast.error(
              'Something went wrong, our team has been notified. Please reload the page and try again later.'
            );
            Sentry.captureException(error);
            console.error(error);
            return reject(error);
          }
          if (data) {
            _setCurrentChat(data.createChat.id);
            setTimeout(() => {
              setCurrentChat(data.createChat.id);
            }, 0);
            resolve(data.createChat);
          }
        });
        open();
        _setCurrentChat('temp-chat-id');
      });
    },
    [execCreateChat, open, setCurrentChat, canWriteDragonAssistant]
  );

  const addPrompt = useCallback(
    async (params: Omit<ChatPromptInput, 'chatId'>) => {
      if (!canWriteDragonAssistant) {
        toast.error('Trial ended. Please upgrade your account to continue using the Dragon Assistant.');
        return Promise.reject(new Error('User does not have permission to use the Dragon Assistant'));
      }
      if (currentChat) {
        open();
        return new Promise((resolve, reject) => {
          execAddPrompt({ input: { chatId: currentChat, ...params } }).then(result => {
            const { data, error } = result;
            if (error) {
              toast.error(
                'Something went wrong, our team has been notified. Please reload the page and try again later.'
              );
              Sentry.captureException(error);
              console.error(error);
              return reject(error);
            }
            if (data) {
              resolve(data);
            }
          });
        });
      } else {
        return createChat(params);
      }
    },
    [execAddPrompt, currentChat, createChat, open, canWriteDragonAssistant]
  );

  useEffect(() => {
    if (currentChat === undefined && data?.chats.data) {
      setCurrentChat(data.chats.data[0]?.id ?? null);
    }
  }, [currentChat, data?.chats.data, setCurrentChat]);

  return (
    <AssistantChatContext.Provider
      value={{
        currentChat,
        setCurrentChat,
        history: data?.chats.data ?? [],
        historyLoading: fetching,
        createChat,
        addPrompt,
        ...rest,
      }}
    >
      {children}
    </AssistantChatContext.Provider>
  );
}
